import { CarIcon, CarProfileIcon } from '@phosphor-icons/react';
import type { MobilityItemDetail } from '@voll-intelligence/genui-contract';
import { fmtTime, fmtWeekdayDate } from '../../../../shared/ui/flight_format.js';
import { useGenUiI18n } from '../../../../shared/ui/i18n.js';

type MobilityLeg = MobilityItemDetail['outward'];

const PLACE = 'min-w-0 truncate text-detail';

function Dot({ hollow = false }: { readonly hollow?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`size-[7px] flex-none rounded-full border-[1.5px] border-foreground ${
        hollow ? 'bg-background' : 'bg-foreground'
      }`}
    />
  );
}

/** A parada: menor e apagada, na mesma coluna dos pontos da partida e do destino. */
function StopDot() {
  return (
    <span aria-hidden="true" className="flex size-[7px] flex-none items-center justify-center">
      <span className="size-[5px] rounded-full bg-muted-foreground" />
    </span>
  );
}

/** O destino que vale mostrar: o carro com motorista fica à disposição, então
 *  só a partida conta (item antigo ainda manda um destino fictício). */
export function destinationOf(leg: MobilityLeg, carDriver: boolean): string | undefined {
  return carDriver ? undefined : leg.to;
}

/**
 * «Corrida · trecho» (#6321, Figma 6859:96903): o cabeçalho do Trecho do voo — glifo do
 * serviço, a data e, só quando há volta (ou no Início do carro com motorista),
 * «· Ida/Volta/Início» (#6699: o serviço e o fornecedor saíram daqui para o cabeçalho do
 * card) —, a hora da saída (18/28) e o percurso: ponto cheio na partida, as paradas na
 * ordem do trajeto e o ponto vazado no destino (que some no carro com motorista).
 */
export function RideLeg({
  leg,
  label,
  carDriver,
}: {
  readonly leg: MobilityLeg;
  /** «Ida»/«Volta»/«Início»; ausente na corrida só de ida. */
  readonly label?: string | undefined;
  readonly carDriver: boolean;
}) {
  const { t, locale } = useGenUiI18n();
  const Icon = carDriver ? CarIcon : CarProfileIcon;
  const destination = destinationOf(leg, carDriver);
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <p className="m-0 flex items-center gap-1.5 text-caption font-medium text-foreground">
        <Icon size={16} className="flex-none" aria-hidden="true" />
        {/* O espaço é do leitor de tela: na fileira flex ele não ocupa lugar. */}
        <span>{fmtWeekdayDate(leg.scheduleDate, locale)}</span>
        {label ? (
          <>
            {' '}
            <span className="text-muted-foreground">· {label}</span>
          </>
        ) : null}
      </p>
      <p className="m-0 flex items-baseline gap-1.5">
        <span className="text-lg leading-7 font-medium text-foreground tabular-nums">
          {fmtTime(leg.scheduleDate)}
        </span>{' '}
        <span className="text-caption font-medium text-muted-foreground">
          {t('productCheckout.mobility.pickup')}
        </span>
      </p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        <li className="flex min-w-0 items-center gap-2">
          <Dot />
          <span className={`${PLACE} text-foreground`}>{leg.from}</span>
        </li>
        {/* As paradas na ordem do trajeto, entre a partida e o destino — a
            mesma ordem da ficha «Detalhes». */}
        {(leg.stops ?? []).map((stop, index) => (
          <li key={`${String(index)}:${stop}`} className="flex min-w-0 items-center gap-2">
            <StopDot />
            <span className={`${PLACE} text-muted-foreground`}>
              <span className="sr-only">{t('productCheckout.mobility.stop')}: </span>
              {stop}
            </span>
          </li>
        ))}
        {destination && (
          <li className="flex min-w-0 items-center gap-2">
            <Dot hollow />
            <span className={`${PLACE} text-foreground`}>{destination}</span>
          </li>
        )}
      </ul>
    </div>
  );
}
